import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Icon } from "./icons";
import type { IconName } from "./icons";

export type ToastTone = "info" | "success" | "warn" | "error";

interface ToastItem {
  id: number;
  tone: ToastTone;
  title?: string;
  message: string;
}

interface ToastOptions {
  title?: string;
  duration?: number;
}

interface ToastApi {
  push: (opts: { tone?: ToastTone; title?: string; message: string; duration?: number }) => void;
  success: (message: string, opts?: ToastOptions) => void;
  error: (message: string, opts?: ToastOptions) => void;
  info: (message: string, opts?: ToastOptions) => void;
  warn: (message: string, opts?: ToastOptions) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

const TONE_ICON: Record<ToastTone, IconName> = {
  info: "info",
  success: "check",
  warn: "alert",
  error: "alert",
};
let _seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Record<number, number>>({});

  const remove = useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
    const handle = timers.current[id];
    if (handle) {
      window.clearTimeout(handle);
      delete timers.current[id];
    }
  }, []);

  const push = useCallback<ToastApi["push"]>(
    (opts) => {
      const id = ++_seq;
      const tone = opts.tone ?? "info";
      setToasts((ts) => [...ts, { id, tone, title: opts.title, message: opts.message }]);
      timers.current[id] = window.setTimeout(() => remove(id), opts.duration ?? 4200);
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (message, o) => push({ tone: "success", message, ...o }),
      error: (message, o) => push({ tone: "error", message, ...o }),
      info: (message, o) => push({ tone: "info", message, ...o }),
      warn: (message, o) => push({ tone: "warn", message, ...o }),
    }),
    [push],
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="ax-toasts" role="region" aria-live="polite" aria-label="Notifications">
        {toasts.map((t) => (
          <div key={t.id} className={`ax-toast ax-toast--${t.tone}`}>
            <span className="ax-toast__ic" aria-hidden="true">
              <Icon name={TONE_ICON[t.tone]} size={13} />
            </span>
            <div className="ax-toast__body">
              {t.title && <div className="ax-toast__title">{t.title}</div>}
              <div className="ax-toast__msg">{t.message}</div>
            </div>
            <button className="ax-toast__x" onClick={() => remove(t.id)} aria-label="Dismiss">
              <Icon name="close" size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}
